import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/BusinessProfileContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SlidersHorizontal, Loader2, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const FIELD_TYPES = { text: "Texte", number: "Nombre", date: "Date", bool: "Oui / Non", list: "Liste", note: "Note" };
const ENTITIES = { client: "Client", appointment: "RDV / intervention", vehicle: "Véhicule / chantier / objet" };

export default function BusinessProfilePage() {
  const { reload } = useProfile();
  const [prof, setProf] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setProf((await api.get("/business-profile")).data);
      setFamilies((await api.get("/professions")).data.families || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch, msg = "Enregistré") => {
    setSaving(true);
    try { const { data } = await api.put("/business-profile", patch); setProf(data); await reload(); toast.success(msg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (loading || !prof) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <PageHeader title="Mon métier & modules" subtitle="Adaptez l'application à votre activité : activez uniquement ce dont vous avez besoin." icon={SlidersHorizontal} />
      <Tabs defaultValue="metier">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="metier" data-testid="bp-tab-metier">Métier</TabsTrigger>
          <TabsTrigger value="modules" data-testid="bp-tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="vocab" data-testid="bp-tab-vocab">Vocabulaire</TabsTrigger>
          <TabsTrigger value="fields" data-testid="bp-tab-fields">Champs personnalisés</TabsTrigger>
        </TabsList>

        <TabsContent value="metier"><MetierTab prof={prof} families={families} save={save} saving={saving} /></TabsContent>
        <TabsContent value="modules"><ModulesTab prof={prof} save={save} saving={saving} /></TabsContent>
        <TabsContent value="vocab"><VocabTab prof={prof} save={save} saving={saving} /></TabsContent>
        <TabsContent value="fields"><FieldsTab prof={prof} save={save} saving={saving} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MetierTab({ prof, families, save, saving }) {
  const [sel, setSel] = useState(prof.profession || "");
  const [custom, setCustom] = useState(prof.profession_custom || "");
  const apply = () => {
    if (sel === "__autre__") {
      if (!custom.trim()) { toast.error("Précisez votre métier"); return; }
      save({ profession: "autre", profession_label: custom.trim(), profession_custom: custom.trim(), apply_defaults: true }, "Métier enregistré");
    } else if (sel) {
      save({ profession: sel, apply_defaults: true }, "Métier enregistré — configuration adaptée appliquée");
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-xl">
      <p className="text-sm text-slate-500">Choisissez votre activité. Une configuration par défaut (modules + vocabulaire) sera appliquée automatiquement — vous pourrez tout ajuster ensuite.</p>
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500">Votre activité</Label>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid="bp-profession-select" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Sélectionner un métier" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200 max-h-[50vh]">
            {families.map((f) => (
              <div key={f.code}>
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">{f.label}</div>
                {f.professions.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
              </div>
            ))}
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Autre</div>
            <SelectItem value="__autre__">Mon métier n'est pas dans la liste</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {sel === "__autre__" && <Input placeholder="Précisez votre métier" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="bp-profession-custom" className="bg-slate-50 border-slate-200" />}
      {prof.profession_label && <p className="text-xs text-slate-500">Métier actuel : <b>{prof.profession_label}</b></p>}
      <Button onClick={apply} disabled={saving} data-testid="bp-profession-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Appliquer la configuration</Button>
    </div>
  );
}

function ModulesTab({ prof, save, saving }) {
  const [mods, setMods] = useState(prof.modules || {});
  const cat = prof.module_catalog || [];
  const groups = [...new Set(cat.map((m) => m.group))];
  const toggle = (k) => setMods({ ...mods, [k]: !mods[k] });
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Activez uniquement les modules nécessaires. <b>Désactiver masque le module</b> — aucune donnée n'est supprimée, tout réapparaît si vous le réactivez.</p>
      {groups.map((g) => (
        <div key={g} className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-3">{g}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {cat.filter((m) => m.group === g).map((m) => {
              const on = mods[m.key] !== false;
              return (
                <button key={m.key} onClick={() => !m.core && toggle(m.key)} disabled={m.core} data-testid={`module-toggle-${m.key}`}
                  className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm text-left ${on ? "bg-emerald-50 border-emerald-200 text-slate-800" : "bg-slate-50 border-slate-200 text-slate-400"} ${m.core ? "opacity-70 cursor-default" : "hover:border-emerald-300"}`}>
                  <span>{m.label}{m.core && <span className="text-[9px] uppercase ml-1 text-slate-400">requis</span>}</span>
                  {on && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <Button onClick={() => save({ modules: mods }, "Modules enregistrés")} disabled={saving} data-testid="bp-modules-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer les modules</Button>
    </div>
  );
}

function VocabTab({ prof, save, saving }) {
  const [v, setV] = useState(prof.vocabulary || {});
  const fields = [
    { k: "object", label: "Terme pour « Véhicule » (singulier)", ph: "Véhicule / Chantier / Objet / Dossier" },
    { k: "object_plural", label: "Terme pour « Véhicules » (pluriel)", ph: "Véhicules / Chantiers…" },
    { k: "appointment", label: "Terme pour « Rendez-vous »", ph: "Rendez-vous / Intervention" },
    { k: "service", label: "Terme pour « Prestation »", ph: "Prestation / Service / Intervention" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-xl">
      <p className="text-sm text-slate-500">Adaptez le vocabulaire à votre métier. Ces libellés s'appliquent dans l'interface.</p>
      {fields.map((f) => (
        <div key={f.k} className="space-y-1.5"><Label className="text-xs text-slate-500">{f.label}</Label>
          <Input value={v[f.k] || ""} placeholder={f.ph} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} data-testid={`vocab-${f.k}`} className="bg-slate-50 border-slate-200" /></div>
      ))}
      <Button onClick={() => save({ vocabulary: v }, "Vocabulaire enregistré")} disabled={saving} data-testid="bp-vocab-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
    </div>
  );
}

function FieldsTab({ prof, save, saving }) {
  const [fields, setFields] = useState(prof.custom_fields || []);
  const add = () => setFields([...fields, { id: `f_${Date.now()}`, label: "", type: "text", entity: "client", required: false, options: [] }]);
  const upd = (i, patch) => setFields(fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Créez vos propres champs (surface terrain, type de chaudière, type d'événement…) rattachés à un client, un RDV ou un véhicule/chantier.</p>
      <Button variant="outline" size="sm" onClick={add} data-testid="field-add" className="border-slate-200"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un champ</Button>
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={f.id} data-testid={`field-row-${i}`} className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Libellé</Label><Input value={f.label} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`field-label-${i}`} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Type</Label>
              <Select value={f.type} onValueChange={(v) => upd(i, { type: v })}><SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{Object.entries(FIELD_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Rattaché à</Label>
              <Select value={f.entity} onValueChange={(v) => upd(i, { entity: v })}><SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{Object.entries(ENTITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} className="accent-emerald-500" /> Obligatoire</label>
              <button onClick={() => setFields(fields.filter((_, idx) => idx !== i))} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            {f.type === "list" && <div className="sm:col-span-4"><Input value={(f.options || []).join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Options séparées par des virgules" className="bg-slate-50 border-slate-200" /></div>}
          </div>
        ))}
      </div>
      <Button onClick={() => save({ custom_fields: fields }, "Champs enregistrés")} disabled={saving} data-testid="bp-fields-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer les champs</Button>
    </div>
  );
}
